
import { supabase } from '../integrations/supabase/client';
import { MedicalRecord } from '../hooks/useMedicalRecords';
import { Vaccination } from '../hooks/useVaccinations';
import { Appointment } from '../hooks/useAppointments';

// Medical Records
export async function getMedicalRecords(petId: string): Promise<MedicalRecord[]> {
  const { data, error } = await supabase
    .from('medical_records')
    .select('*')
    .eq('pet_id', petId)
    .order('record_date', { ascending: false });

  if (error) {
    throw new Error(`Error fetching medical records: ${error.message}`);
  }

  return data || [];
}

export async function getMedicalRecordById(recordId: string): Promise<MedicalRecord> {
  console.log('Fetching medical record with ID:', recordId);
  
  // First get the medical record
  const { data: record, error: recordError } = await supabase
    .from('medical_records')
    .select('*')
    .eq('id', recordId)
    .single();

  if (recordError) {
    console.error("Error fetching medical record:", recordError);
    throw new Error(`Error fetching medical record: ${recordError.message}`);
  }

  console.log('Medical record found:', record);

  // Try multiple approaches to get attachments
  let attachments: string[] = [];

  if (record.has_attachments) {
    console.log('Record has attachments, attempting to fetch...');
    
    // Try to get attachments from media_files table using different approaches
    const queries = [
      // Query 1: Using post_id (medical record ID)
      supabase
        .from('media_files')
        .select('url, file_path')
        .eq('media_type', 'document_medical')
        .eq('post_id', recordId),
      
      // Query 2: Using pet_id and user_id filters
      supabase
        .from('media_files')
        .select('url, file_path')
        .eq('media_type', 'document_medical')
        .eq('pet_id', record.pet_id)
        .eq('user_id', record.user_id),
      
      // Query 3: Just by media type and user
      supabase
        .from('media_files')
        .select('url, file_path')
        .eq('media_type', 'document_medical')
        .eq('user_id', record.user_id)
    ];

    for (let i = 0; i < queries.length; i++) {
      console.log(`Trying query approach ${i + 1}...`);
      const { data: mediaFiles, error: mediaError } = await queries[i];
      
      if (mediaError) {
        console.error(`Query ${i + 1} error:`, mediaError);
        continue;
      }
      
      console.log(`Query ${i + 1} results:`, mediaFiles);
      
      if (mediaFiles && mediaFiles.length > 0) {
        // Filter files that might be related to this record based on timing
        const recordDate = new Date(record.created_at);
        const potentialFiles = mediaFiles.filter(file => {
          if (!file.url && !file.file_path) return false;
          return true; // For now, include all medical documents for this user
        });
        
        if (potentialFiles.length > 0) {
          attachments = potentialFiles.map(file => file.url || file.file_path).filter(Boolean);
          console.log(`Found ${attachments.length} attachments using query ${i + 1}`);
          break;
        }
      }
    }

    if (attachments.length === 0) {
      console.log('No attachments found in media_files table');
      
      // Check if there are any files in the medical-documents bucket for this user
      const { data: bucketFiles, error: bucketError } = await supabase.storage
        .from('medical-documents')
        .list(`${record.user_id}/`, {
          limit: 100,
          offset: 0,
        });

      if (!bucketError && bucketFiles && bucketFiles.length > 0) {
        console.log('Found files in medical-documents bucket:', bucketFiles);
        
        // Generate public URLs for the files
        const fileUrls = bucketFiles
          .filter(file => !file.name.endsWith('/')) // Exclude folders
          .map(file => {
            const { data } = supabase.storage
              .from('medical-documents')
              .getPublicUrl(`${record.user_id}/${file.name}`);
            return data.publicUrl;
          });
        
        attachments = fileUrls;
        console.log('Generated URLs from bucket files:', attachments);
      }
    }
  }

  const result = { ...record, attachments };
  console.log('Final medical record with attachments:', result);
  
  return result;
}

export async function saveMedicalRecord(
  petId: string,
  recordData: any, 
  recordId?: string
) {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user || !user.id) {
      throw new Error("User not authenticated");
    }

    // Check prescription upload permission if there are attachments
    if (!recordId && recordData.attachments && recordData.attachments.length > 0) {
      const { data: userData } = await supabase
        .from('users')
        .select('subscription_tier')
        .eq('id', user.id)
        .single();

      if (userData) {
        const { data: tierData } = await supabase
          .from('tier_limits')
          .select('allow_prescription_uploads')
          .eq('tier', userData.subscription_tier)
          .single();

        if (tierData && !tierData.allow_prescription_uploads) {
          throw new Error('Your subscription tier does not allow uploading prescription documents. Please upgrade to Standard or higher.');
        }
      }
    }

    // Handle file uploads first if there are new files
    let uploadedFiles: Array<{ url: string; size: number; name: string }> = [];
    if (recordData.attachments && recordData.attachments.length > 0) {
      console.log('Uploading files:', recordData.attachments);
      
      for (let i = 0; i < recordData.attachments.length; i++) {
        const file = recordData.attachments[i];
        const fileName = `${user.id}/${Date.now()}-${file.name}`;
        
        console.log('Uploading file:', fileName);
        
        // Upload file to storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('medical-documents')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          throw new Error(`Error uploading file: ${uploadError.message}`);
        }

        console.log('File uploaded successfully:', uploadData);

        // Get public URL
        const { data: urlData } = supabase.storage
          .from('medical-documents')
          .getPublicUrl(fileName);

        uploadedFiles.push({
          url: urlData.publicUrl,
          size: file.size,
          name: file.name
        });
        console.log('File URL generated:', urlData.publicUrl);
      }
    }

    const recordInfo = {
      pet_id: petId,
      title: recordData.title,
      record_type: recordData.record_type,
      record_date: recordData.record_date,
      description: recordData.description,
      vet_name: recordData.vet_name,
      has_attachments: uploadedFiles.length > 0 || 
                      (recordData.existingAttachments && recordData.existingAttachments.length > 0)
    };

    let result;
    if (recordId) {
      // Update existing record
      const { data, error } = await supabase
        .from('medical_records')
        .update(recordInfo)
        .eq('id', recordId)
        .select()
        .single();

      if (error) throw new Error(`Error updating medical record: ${error.message}`);
      result = data;
    } else {
      // Insert new record
      const { data, error } = await supabase
        .from('medical_records')
        .insert({
          ...recordInfo,
          user_id: user.id
        })
        .select()
        .single();

      if (error) throw new Error(`Error creating medical record: ${error.message}`);
      result = data;
    }

    // Create media_files entries for uploaded files
    if (uploadedFiles.length > 0) {
      console.log('Creating media_files entries for uploaded files');
      
      const mediaEntries = uploadedFiles.map(fileInfo => ({
        user_id: user.id,
        pet_id: petId,
        post_id: result.id, // Link to medical record
        media_type: 'document_medical' as const,
        url: fileInfo.url,
        file_path: fileInfo.name,
        size_bytes: fileInfo.size,
        mime_type: fileInfo.name.endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream'
      }));

      const { error: mediaError } = await supabase
        .from('media_files')
        .insert(mediaEntries);

      if (mediaError) {
        console.error('Error creating media entries:', mediaError);
        // Don't throw here as the record was created successfully
      } else {
        console.log('Media entries created successfully');
      }
    }

    return result;
  } catch (error) {
    console.error("Error saving medical record:", error);
    throw error;
  }
}

// Vaccinations
export async function getVaccinations(petId: string): Promise<Vaccination[]> {
  const { data, error } = await supabase
    .from('vaccinations')
    .select('*')
    .eq('pet_id', petId)
    .order('date_due', { ascending: true });

  if (error) {
    throw new Error(`Error fetching vaccinations: ${error.message}`);
  }

  return data || [];
}

export async function getVaccinationById(vaccinationId: string): Promise<Vaccination> {
  const { data, error } = await supabase
    .from('vaccinations')
    .select('*')
    .eq('id', vaccinationId)
    .single();

  if (error) {
    throw new Error(`Error fetching vaccination: ${error.message}`);
  }

  return data;
}

export async function saveMedicalVaccination(
  petId: string,
  vaccinationData: any,
  vaccinationId?: string
) {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user || !user.id) {
      throw new Error("User not authenticated");
    }

    // Format date for Postgres
    const formatDate = (date: Date | null) => {
      return date ? date.toISOString() : null;
    };

    const vaccinationInfo = {
      pet_id: petId,
      vaccine_name: vaccinationData.vaccine_name,
      date_due: formatDate(vaccinationData.date_due),
      date_given: formatDate(vaccinationData.date_given),
      vet_name: vaccinationData.vet_name,
      notes: vaccinationData.notes
    };

    if (vaccinationId) {
      // Update existing vaccination
      const { data, error } = await supabase
        .from('vaccinations')
        .update(vaccinationInfo)
        .eq('id', vaccinationId)
        .select();

      if (error) throw new Error(`Error updating vaccination: ${error.message}`);
      return data;
    } else {
      // Insert new vaccination
      const { data, error } = await supabase
        .from('vaccinations')
        .insert({
          ...vaccinationInfo,
          user_id: user.id
        })
        .select();

      if (error) throw new Error(`Error creating vaccination: ${error.message}`);
      return data;
    }
  } catch (error) {
    console.error("Error saving vaccination:", error);
    throw error;
  }
}

// Appointments
export async function getAppointments(petId: string): Promise<Appointment[]> {
  const { data, error } = await supabase
    .from('appointments')
    .select('*')
    .eq('pet_id', petId)
    .order('appointment_datetime', { ascending: true });

  if (error) {
    throw new Error(`Error fetching appointments: ${error.message}`);
  }

  return data || [];
}

export async function getAppointmentById(appointmentId: string): Promise<Appointment> {
  const { data, error } = await supabase
    .from('appointments')
    .select('*')
    .eq('id', appointmentId)
    .single();

  if (error) {
    throw new Error(`Error fetching appointment: ${error.message}`);
  }

  return data;
}

export async function saveAppointment(
  petId: string,
  appointmentData: any,
  appointmentId?: string
) {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user || !user.id) {
      throw new Error("User not authenticated");
    }

    // The appointment data object with properly formatted datetime
    const appointmentInfo = {
      pet_id: petId,
      appointment_type: appointmentData.appointment_type,
      appointment_datetime: appointmentData.appointment_datetime.toISOString(),
      status: appointmentData.status,
      vet_name: appointmentData.vet_name,
      vet_phone: appointmentData.vet_phone,
      location: appointmentData.location,
      notes: appointmentData.notes
    };

    if (appointmentId) {
      // Update existing appointment
      const { data, error } = await supabase
        .from('appointments')
        .update(appointmentInfo)
        .eq('id', appointmentId)
        .select();

      if (error) throw new Error(`Error updating appointment: ${error.message}`);
      return data;
    } else {
      // Insert new appointment
      const { data, error } = await supabase
        .from('appointments')
        .insert({
          ...appointmentInfo,
          user_id: user.id
        })
        .select();

      if (error) throw new Error(`Error creating appointment: ${error.message}`);
      return data;
    }
  } catch (error) {
    console.error("Error saving appointment:", error);
    throw error;
  }
}

// Delete functions
export const deleteMedicalRecord = async (recordId: string): Promise<void> => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('User not authenticated');

  // Get the record to check for attachments
  const { data: record, error: fetchError } = await supabase
    .from('medical_records')
    .select('has_attachments')
    .eq('id', recordId)
    .single();

  if (fetchError) throw fetchError;

  // Delete associated media files if any
  if (record?.has_attachments) {
    const { error: mediaError } = await supabase
      .from('media_files')
      .delete()
      .eq('user_id', user.user.id)
      .eq('post_id', recordId);

    if (mediaError) console.warn('Error deleting media files:', mediaError);
  }

  // Delete the medical record
  const { error } = await supabase
    .from('medical_records')
    .delete()
    .eq('id', recordId)
    .eq('user_id', user.user.id);

  if (error) throw error;
};

export const deleteVaccination = async (vaccinationId: string): Promise<void> => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('User not authenticated');

  const { error } = await supabase
    .from('vaccinations')
    .delete()
    .eq('id', vaccinationId)
    .eq('user_id', user.user.id);

  if (error) throw error;
};

export const deleteAppointment = async (appointmentId: string): Promise<void> => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('User not authenticated');

  const { error } = await supabase
    .from('appointments')
    .delete()
    .eq('id', appointmentId)
    .eq('user_id', user.user.id);

  if (error) throw error;
};
